import { Fragment, useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../api/client'
import { useHinweise } from '../hinweise/HinweisContext'

interface Kurzansicht {
  id: number
  titel: string
  thema: string | null
  status: 'OFFEN' | 'ABGESCHLOSSEN'
  erstelltAm: string
  gelesenAm: string | null
  fragen: number
  offen: number
  beantwortet: number
}

interface Verweis {
  beschriftung: string
  pfad: string
}

interface Frage {
  id: number
  reihenfolge: number
  text: string
  art: 'AUSWAHL' | 'JA_NEIN' | 'FREITEXT'
  status: 'OFFEN' | 'BEANTWORTET' | 'UMGESETZT' | 'ZURUECKGEZOGEN'
  optionen: { schluessel: string; beschriftung: string }[]
  verweise: Verweis[]
  antwortWahl: string | null
  antwortText: string | null
  beantwortetVon: string | null
  beantwortetAm: string | null
  umsetzung: string | null
  umgesetztAm: string | null
}

interface Vorgang {
  id: number
  titel: string
  bericht: string | null
  thema: string | null
  status: 'OFFEN' | 'ABGESCHLOSSEN'
  erstelltVon: string
  erstelltAm: string
  gelesenAm: string | null
  verweise: Verweis[]
  fragen: Frage[]
}

function datum(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : ''
}

/** **fett** innerhalb einer Zeile. */
function zeile(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((teil, i) =>
    teil.startsWith('**') && teil.endsWith('**') && teil.length > 4 ? (
      <strong key={i}>{teil.slice(2, -2)}</strong>
    ) : (
      <Fragment key={i}>{teil}</Fragment>
    ),
  )
}

/**
 * Der Bericht des Assistenten in schlichtem Markdown: Absätze, Überschriften, Listen, fett.
 *
 * Bewusst als React-Elemente und nicht über innerHTML: Der Text kommt von außen, und eine
 * Markdown-Bibliothek brächte für vier Auszeichnungen eine Angriffsfläche mit, die hier
 * niemand braucht.
 */
function Bericht({ text }: { text: string }) {
  const bloecke: ReactNode[] = []
  let liste: string[] = []
  const listeAbschliessen = () => {
    if (liste.length) {
      bloecke.push(
        <ul key={`l${bloecke.length}`}>
          {liste.map((p, i) => (
            <li key={i}>{zeile(p)}</li>
          ))}
        </ul>,
      )
      liste = []
    }
  }
  for (const roh of text.split('\n')) {
    const z = roh.trimEnd()
    if (/^\s*[-*] /.test(z)) {
      liste.push(z.replace(/^\s*[-*] /, ''))
      continue
    }
    listeAbschliessen()
    if (!z.trim()) continue
    const ueberschrift = z.match(/^#{1,4} (.*)$/)
    bloecke.push(
      ueberschrift ? (
        <h4 key={bloecke.length} style={{ margin: '12px 0 4px' }}>
          {zeile(ueberschrift[1])}
        </h4>
      ) : (
        <p key={bloecke.length} style={{ margin: '6px 0' }}>
          {zeile(z)}
        </p>
      ),
    )
  }
  listeAbschliessen()
  return <div>{bloecke}</div>
}

function Verweise({ verweise }: { verweise: Verweis[] }) {
  if (verweise.length === 0) return null
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8, fontSize: '0.88rem' }}>
      {verweise.map((v) => (
        <Link key={v.pfad + v.beschriftung} to={v.pfad}>
          → {v.beschriftung}
        </Link>
      ))}
    </div>
  )
}

const STATUS_ETIKETT: Record<Frage['status'], [string, string]> = {
  OFFEN: ['orange', 'offen'],
  BEANTWORTET: ['navy', 'beantwortet — wartet auf den Assistenten'],
  UMGESETZT: ['gruen', 'umgesetzt'],
  ZURUECKGEZOGEN: ['grau', 'zurückgezogen'],
}

function FrageKarte({ frage, onAntwort }: { frage: Frage; onAntwort: (f: Frage, wahl: string | null, text: string) => Promise<void> }) {
  const [wahl, setWahl] = useState<string | null>(frage.antwortWahl)
  const [text, setText] = useState(frage.antwortText ?? '')
  const [sendet, setSendet] = useState(false)
  const [aendern, setAendern] = useState(false)
  const [etikettKlasse, etikettText] = STATUS_ETIKETT[frage.status]

  // Beantwortbar, solange der Assistent nicht umgesetzt hat. Eine bereits gegebene Antwort
  // zeigt sich zuerst als Ergebnis; ändern nur auf ausdrücklichen Wunsch.
  const eingabe = frage.status === 'OFFEN' || (frage.status === 'BEANTWORTET' && aendern)

  const optionen =
    frage.art === 'JA_NEIN'
      ? [
          { schluessel: 'ja', beschriftung: 'Ja' },
          { schluessel: 'nein', beschriftung: 'Nein' },
        ]
      : frage.optionen
  const antwortBeschriftung =
    optionen.find((o) => o.schluessel === frage.antwortWahl)?.beschriftung ?? null
  const bereit = frage.art === 'FREITEXT' ? text.trim() !== '' : wahl !== null

  async function senden() {
    setSendet(true)
    try {
      await onAntwort(frage, frage.art === 'FREITEXT' ? null : wahl, text)
      setAendern(false)
    } finally {
      setSendet(false)
    }
  }

  return (
    <div className="karte abschnitt" style={{ padding: '14px 18px' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
        <strong style={{ color: 'var(--grau-500)' }}>{frage.reihenfolge}</strong>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>{zeile(frage.text)}</div>
          <Verweise verweise={frage.verweise} />
        </div>
        <span className={`etikett ${etikettKlasse}`}>{etikettText}</span>
      </div>

      {eingabe ? (
        <div style={{ marginTop: 10, marginLeft: 22 }}>
          {frage.art !== 'FREITEXT' &&
            optionen.map((o) => (
              <label key={o.schluessel} style={{ display: 'block', margin: '4px 0', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name={`frage-${frage.id}`}
                  checked={wahl === o.schluessel}
                  onChange={() => setWahl(o.schluessel)}
                  style={{ marginRight: 8 }}
                />
                {o.beschriftung}
              </label>
            ))}
          <textarea
            rows={frage.art === 'FREITEXT' ? 3 : 1}
            placeholder={frage.art === 'FREITEXT' ? 'Ihre Antwort' : 'Kommentar (freiwillig)'}
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={{
              width: '100%',
              marginTop: 6,
              padding: '7px 10px',
              border: '1px solid var(--grau-300)',
              borderRadius: 'var(--radius-klein)',
              fontFamily: 'inherit',
            }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button className="knopf-primaer" disabled={!bereit || sendet} onClick={() => void senden()}>
              {sendet ? 'Wird gesendet …' : 'Antwort senden'}
            </button>
            {aendern && (
              <button className="knopf-schlicht" onClick={() => setAendern(false)}>
                Abbrechen
              </button>
            )}
          </div>
        </div>
      ) : (
        frage.beantwortetAm && (
          <div style={{ marginTop: 8, marginLeft: 22, fontSize: '0.9rem' }}>
            <div>
              ✓ {frage.beantwortetVon ?? 'Unbekannt'}, {datum(frage.beantwortetAm)}:{' '}
              <strong>{antwortBeschriftung ?? frage.antwortText}</strong>
              {antwortBeschriftung && frage.antwortText && <> — „{frage.antwortText}“</>}
              {frage.status === 'BEANTWORTET' && (
                <button
                  className="knopf-schlicht"
                  style={{ padding: '2px 8px', marginLeft: 8, fontSize: '0.78rem' }}
                  onClick={() => setAendern(true)}
                >
                  ändern
                </button>
              )}
            </div>
            {frage.umsetzung && (
              <div style={{ marginTop: 4, color: 'var(--grau-700)' }}>
                {frage.status === 'UMGESETZT' ? '✓ Umgesetzt' : 'Zurückgezogen'} {datum(frage.umgesetztAm)}:{' '}
                {frage.umsetzung}
              </div>
            )}
          </div>
        )
      )}
      {!frage.beantwortetAm && frage.umsetzung && (
        <div style={{ marginTop: 8, marginLeft: 22, fontSize: '0.9rem', color: 'var(--grau-700)' }}>
          Zurückgezogen {datum(frage.umgesetztAm)}: {frage.umsetzung}
        </div>
      )}
    </div>
  )
}

/**
 * Klärungen: Berichte und Fragen des Assistenten an die Disposition.
 *
 * Der Assistent übernimmt Datenlieferungen und stößt dabei auf Dinge, die nur der Betrieb
 * entscheiden kann. Hier stehen sie mit dem Zusammenhang, und hier wird geantwortet — der
 * Assistent holt die Antworten ab und vermerkt an jeder Frage, was er daraufhin getan hat.
 */
export function Klaerungen() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { aktualisiere } = useHinweise()
  const [liste, setListe] = useState<Kurzansicht[] | null>(null)
  const [vorgang, setVorgang] = useState<Vorgang | null>(null)
  const [meldung, setMeldung] = useState<{ art: string; text: string } | null>(null)

  const listeLaden = useCallback(async () => {
    try {
      setListe(await api.get<Kurzansicht[]>('/api/klaerungen'))
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Laden fehlgeschlagen' })
    }
  }, [])

  useEffect(() => {
    void listeLaden()
  }, [listeLaden])

  useEffect(() => {
    if (!id) {
      setVorgang(null)
      return
    }
    api
      .get<Vorgang>(`/api/klaerungen/${id}`)
      .then((v) => {
        setVorgang(v)
        // Das Öffnen vermerkt den Vorgang als gelesen — die Liste soll das gleich zeigen.
        void listeLaden()
      })
      .catch((ex) => setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Laden fehlgeschlagen' }))
  }, [id, listeLaden])

  // Ohne Auswahl den jüngsten offenen Vorgang öffnen — meist ist das der, um den es geht.
  useEffect(() => {
    if (!id && liste && liste.length > 0) {
      const erster = liste.find((k) => k.status === 'OFFEN') ?? liste[0]
      navigate(`/klaerungen/${erster.id}`, { replace: true })
    }
  }, [id, liste, navigate])

  async function antworten(f: Frage, wahl: string | null, text: string) {
    if (!vorgang) return
    setMeldung(null)
    try {
      const neu = await api.post<Vorgang>(`/api/klaerungen/${vorgang.id}/fragen/${f.id}/antwort`, {
        wahl,
        text,
      })
      setVorgang(neu)
      setMeldung({ art: 'erfolg', text: 'Antwort gespeichert. Der Assistent holt sie beim nächsten Durchlauf ab.' })
      void listeLaden()
      aktualisiere()
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Senden fehlgeschlagen' })
    }
  }

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Klärungen</h1>
          <p>Berichte und Fragen des Assistenten. Ihre Antworten setzt er beim nächsten Durchlauf um.</p>
        </div>
      </div>

      {meldung && <div className={`meldung ${meldung.art}`}>{meldung.text}</div>}

      {liste && liste.length === 0 ? (
        <div className="karte abschnitt leer">Der Assistent hat noch nichts vorgelegt.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 320px) 1fr', gap: 18, alignItems: 'start' }}>
          <div className="karte" style={{ padding: 0, overflow: 'hidden' }}>
            {(liste ?? []).map((k) => (
              <Link
                key={k.id}
                to={`/klaerungen/${k.id}`}
                style={{
                  display: 'block',
                  padding: '10px 14px',
                  borderBottom: '1px solid var(--grau-100, #eee)',
                  background: String(k.id) === id ? 'var(--grau-100, #f3f4f6)' : undefined,
                  color: 'inherit',
                  textDecoration: 'none',
                }}
              >
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ color: k.status === 'OFFEN' ? 'var(--orange-500, #d97706)' : 'var(--grau-300)' }}>●</span>
                  <strong style={{ fontWeight: k.gelesenAm ? 500 : 700 }}>{k.titel}</strong>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--grau-500)', marginTop: 2 }}>
                  {k.thema ? `${k.thema} · ` : ''}
                  {datum(k.erstelltAm)}
                  {k.offen > 0 && ` · ${k.offen} offen`}
                  {k.beantwortet > 0 && ` · ${k.beantwortet} beantwortet`}
                </div>
              </Link>
            ))}
          </div>

          <div>
            {vorgang && (
              <>
                <div className="karte abschnitt">
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <h2 style={{ margin: 0 }}>{vorgang.titel}</h2>
                    <span className={`etikett ${vorgang.status === 'OFFEN' ? 'orange' : 'gruen'}`}>
                      {vorgang.status === 'OFFEN' ? 'offen' : 'abgeschlossen'}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--grau-500)', margin: '4px 0 8px' }}>
                    {vorgang.thema ? `${vorgang.thema} · ` : ''}
                    {vorgang.erstelltVon}, {datum(vorgang.erstelltAm)}
                  </div>
                  {vorgang.bericht && <Bericht text={vorgang.bericht} />}
                  <Verweise verweise={vorgang.verweise} />
                </div>
                {vorgang.fragen.map((f) => (
                  <FrageKarte key={`${f.id}-${f.status}-${f.beantwortetAm ?? ''}`} frage={f} onAntwort={antworten} />
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}
